import React from 'react';
import './DataStructures.css';

const DataStructures = () => {
  return (
    <div className="data-structure-section">
      <h2>Data Structures</h2>

      <div className="topic-section">
        <h3>Array</h3>
        <p>
          An array is a data structure consisting of a collection of elements
          (values or variables), each identified by at least one array index or
          key. Arrays are fixed in size after they are created.
        </p>

        <h4>Array Creation in Java</h4>
        <div className="code-snippet">
          {`// Declare an array of integers
int[] anArray;

// Allocate memory for 10 integers
anArray = new int[10];

// Initialize first element
anArray[0] = 100;

// Or, create and initialize in one line
int[] anotherArray = { 1, 2, 3, 4, 5 };`}
        </div>

        <h4>Key Characteristics</h4>
        <ul>
            <li><strong>Time Complexity:</strong> Accessing an element by index is very fast, with a time complexity of O(1). Searching for an element is O(n).</li>
            <li><strong>Size:</strong> Fixed. You cannot change the size of an array after creation.</li>
            <li><strong>Use Case:</strong> Good for when you have a fixed number of elements and need fast access to them.</li>
        </ul>
      </div>

      <div className="topic-section">
        <h3>ArrayList</h3>
        <p>
          An `ArrayList` is a resizable array implementation in Java. It provides dynamic arrays, meaning the size can grow or shrink as needed.
        </p>

        <h4>ArrayList Creation in Java</h4>
        <div className="code-snippet">
          {`import java.util.ArrayList;
import java.util.List;

// Create an ArrayList of Strings
List<String> list = new ArrayList<>();

// Add elements
list.add("Apple");
list.add("Banana");
list.add("Cherry");

// Access elements
String fruit = list.get(0); // "Apple"`}
        </div>
        <h4>Key Characteristics</h4>
        <ul>
            <li><strong>Time Complexity:</strong> Get/Set is O(1) on average. Add/Remove is O(n) in the worst case (if the internal array needs to be resized), but amortized O(1) for adding to the end.</li>
            <li><strong>Size:</strong> Dynamic.</li>
            <li><strong>Use Case:</strong> The most common list implementation. Good for most cases where you need a dynamic list of elements.</li>
        </ul>
      </div>

      <div className="topic-section">
        <h3>LinkedList</h3>
        <p>
          A `LinkedList` is a linear data structure where the elements are not stored in contiguous locations. Each element is a separate object with a data part and an address part.
        </p>

        <h4>LinkedList Creation in Java</h4>
        <div className="code-snippet">
          {`import java.util.LinkedList;
import java.util.List;

// Create a LinkedList of Strings
List<String> list = new LinkedList<>();

// Add elements
list.add("Dog");
list.add("Cat");
list.add("Cow");

// Access elements (less efficient than ArrayList)
String animal = list.get(1); // "Cat"`}
        </div>
        <h4>Key Characteristics</h4>
        <ul>
            <li><strong>Time Complexity:</strong> Get/Set is O(n). Add/Remove at the beginning or end is O(1). Adding/removing in the middle is O(n) to find the element, but the insertion/deletion itself is O(1).</li>
            <li><strong>Size:</strong> Dynamic.</li>
            <li><strong>Use Case:</strong> Best for when you have a large number of add/remove operations from the beginning or end of the list (e.g., implementing a queue).</li>
        </ul>
        <h4>ArrayList vs. LinkedList</h4>
        <p>For a senior engineer interview, you must know the trade-offs: `ArrayList` is generally faster for access, while `LinkedList` is faster for additions and removals at the ends of the list.</p>
      </div>

      <div className="topic-section">
        <h3>HashMap</h3>
        <p>
          A `HashMap` is a data structure that uses a hash function to map identifying values, known as keys, to their associated values. It does not guarantee any specific order of the elements.
        </p>

        <h4>HashMap Creation in Java</h4>
        <div className="code-snippet">
          {`import java.util.HashMap;
import java.util.Map;

// Create a HashMap with String keys and Integer values
Map<String, Integer> map = new HashMap<>();

// Add key-value pairs
map.put("One", 1);
map.put("Two", 2);
map.put("Three", 3);

// Get a value by key
Integer value = map.get("Two"); // 2`}
        </div>
        <h4>Key Characteristics</h4>
        <ul>
            <li><strong>Time Complexity:</strong> Get/Put is O(1) on average, assuming a good hash function. In the worst case (many hash collisions), it can degrade to O(n).</li>
            <li><strong>Ordering:</strong> Unordered. If you need insertion order, use `LinkedHashMap`. If you need sorted order, use `TreeMap`.</li>
            <li><strong>Use Case:</strong> Excellent for lookups. Anytime you need to find an object by a key.</li>
        </ul>
      </div>

      <div className="topic-section">
        <h3>TreeMap</h3>
        <p>
            A `TreeMap` is a map implementation that keeps its entries sorted according to the natural ordering of its keys, or by a `Comparator` provided at map creation time.
        </p>
        <h4>TreeMap Creation in Java</h4>
        <div className="code-snippet">
          {`import java.util.TreeMap;
import java.util.Map;

// Create a TreeMap
Map<String, Integer> map = new TreeMap<>();

map.put("C", 3);
map.put("A", 1);
map.put("B", 2);

// The keys will be iterated in sorted order: "A", "B", "C"
for (String key : map.keySet()) {
    System.out.println(key);
}`}
        </div>
        <h4>Key Characteristics</h4>
        <ul>
            <li><strong>Time Complexity:</strong> Get/Put/Remove operations are O(log n). It is based on a Red-Black Tree structure.</li>
            <li><strong>Ordering:</strong> Sorted.</li>
            <li><strong>Use Case:</strong> Use when you need a map that is always sorted by key. Useful for range queries (e.g., get all keys between "A" and "D").</li>
        </ul>
      </div>

      <div className="topic-section">
        <h3>ConcurrentHashMap</h3>
        <p>
            A `ConcurrentHashMap` is a thread-safe map implementation. It is the go-to choice for concurrent applications. It does NOT lock the entire map for every modification. Instead, it divides the map into segments (or nodes in modern Java) and locks only the relevant segment.
        </p>
        <h4>ConcurrentHashMap vs. synchronized HashMap</h4>
        <p>A common interview question is to compare `ConcurrentHashMap` with `Collections.synchronizedMap(new HashMap{'<'}
{'>'}
())`. The synchronized map locks the entire collection for every read or write, leading to poor performance under high concurrency. `ConcurrentHashMap` offers much higher throughput.</p>
        <h4>ConcurrentHashMap Creation in Java</h4>
        <div className="code-snippet">
          {`import java.util.concurrent.ConcurrentHashMap;
import java.util.Map;

// Create a ConcurrentHashMap
Map<String, Integer> map = new ConcurrentHashMap<>();

map.put("One", 1);
map.put("Two", 2);

// This map can be safely accessed and modified by multiple threads.`}
        </div>
        <h4>Key Characteristics</h4>
        <ul>
            <li><strong>Thread Safety:</strong> Fully thread-safe.</li>
            <li><strong>Performance:</strong> High concurrency for reads and writes. Reads are generally non-blocking.</li>
            <li><strong>Nulls:</strong> Does not allow `null` keys or values.</li>
            <li><strong>Use Case:</strong> The standard map for multi-threaded applications. Caches, shared application state, etc.</li>
        </ul>
      </div>
    </div>
  );
};

export default DataStructures;